
import React, { useEffect, useState, useRef } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Volume2, VolumeX, Wifi, WifiOff } from 'lucide-react';

export interface AudioQualityMetrics {
  volumeLevel: number;
  noiseLevel: number;
  signalToNoise: number;
  clarity: number;
  stability: number;
}

interface AudioQualityMonitorProps {
  stream?: MediaStream;
  onQualityChange?: (metrics: AudioQualityMetrics) => void;
  showVisualIndicators?: boolean;
}

const AudioQualityMonitor: React.FC<AudioQualityMonitorProps> = ({
  stream,
  onQualityChange,
  showVisualIndicators = true
}) => {
  const [metrics, setMetrics] = useState<AudioQualityMetrics>({
    volumeLevel: 0,
    noiseLevel: 0,
    signalToNoise: 0,
    clarity: 0,
    stability: 0
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (!stream) return;

    const initializeAudioAnalysis = async () => {
      try {
        audioContextRef.current = new AudioContext();
        analyzerRef.current = audioContextRef.current.createAnalyser();
        
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyzerRef.current);
        
        analyzerRef.current.fftSize = 2048;
        analyzerRef.current.smoothingTimeConstant = 0.8;
        
        startAnalysis();
      } catch (error) {
        console.error('Error initializing audio analysis:', error);
      }
    };

    initializeAudioAnalysis();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stream]);

  const startAnalysis = () => {
    if (!analyzerRef.current) return;

    const bufferLength = analyzerRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const frequencyArray = new Uint8Array(bufferLength);

    const analyze = () => {
      if (!analyzerRef.current) return;

      analyzerRef.current.getByteTimeDomainData(dataArray);
      analyzerRef.current.getByteFrequencyData(frequencyArray);

      // Calculate volume level
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const sample = (dataArray[i] - 128) / 128;
        sum += sample * sample;
      }
      const volumeLevel = Math.sqrt(sum / bufferLength) * 100;

      // Calculate noise level (low frequencies)
      const lowFreqSum = frequencyArray.slice(0, bufferLength / 8)
        .reduce((sum, val) => sum + val, 0);
      const noiseLevel = (lowFreqSum / (bufferLength / 8)) / 255 * 100;

      // Calculate signal-to-noise ratio
      const signalToNoise = volumeLevel > 0 ? Math.max(0, volumeLevel - noiseLevel) : 0;

      // Calculate clarity (mid-high frequencies)
      const midHighFreqSum = frequencyArray.slice(bufferLength / 4, bufferLength / 2)
        .reduce((sum, val) => sum + val, 0);
      const clarity = (midHighFreqSum / (bufferLength / 4)) / 255 * 100;

      // Calculate stability (variance in volume)
      const stability = Math.max(0, 100 - (volumeLevel > 10 ? Math.abs(volumeLevel - 50) : 50));

      const newMetrics: AudioQualityMetrics = {
        volumeLevel: Math.round(volumeLevel),
        noiseLevel: Math.round(noiseLevel),
        signalToNoise: Math.round(signalToNoise),
        clarity: Math.round(clarity),
        stability: Math.round(stability)
      };

      setMetrics(newMetrics);
      onQualityChange?.(newMetrics);

      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  const getQualityStatus = (value: number) => {
    if (value >= 70) return { label: 'Excelente', variant: 'default' as const };
    if (value >= 50) return { label: 'Boa', variant: 'secondary' as const };
    if (value >= 30) return { label: 'Regular', variant: 'outline' as const };
    return { label: 'Ruim', variant: 'destructive' as const };
  };

  if (!showVisualIndicators) return null;

  return (
    <div className="space-y-3 p-3 bg-white/50 backdrop-blur-sm rounded-lg border">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Volume2 className="w-4 h-4" />
          Qualidade do Áudio
        </h4>
        <Badge {...getQualityStatus(metrics.signalToNoise)}>
          {getQualityStatus(metrics.signalToNoise).label}
        </Badge>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span>Volume</span>
          <span>{metrics.volumeLevel}%</span>
        </div>
        <Progress value={metrics.volumeLevel} className="h-1" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span>Ruído</span>
          <span>{metrics.noiseLevel}%</span>
        </div>
        <Progress value={metrics.noiseLevel} className="h-1" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span>Clareza</span>
          <span>{metrics.clarity}%</span>
        </div>
        <Progress value={metrics.clarity} className="h-1" />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1">
          {metrics.signalToNoise > 30 ? (
            <Wifi className="w-3 h-3 text-green-500" />
          ) : (
            <WifiOff className="w-3 h-3 text-red-500" />
          )}
          <span>S/N: {metrics.signalToNoise}%</span>
        </div>
        <div className="flex items-center gap-1">
          {metrics.stability > 50 ? (
            <Volume2 className="w-3 h-3 text-green-500" />
          ) : (
            <VolumeX className="w-3 h-3 text-orange-500" />
          )}
          <span>Estab: {metrics.stability}%</span>
        </div>
      </div>
    </div>
  );
};

export default AudioQualityMonitor;
